import React, { cloneElement, isValidElement } from 'react'
import PropTypes from 'prop-types'
import {
  Button as MuiButton,
  IconButton,
  Typography,
  CircularProgress
} from '@mui/material'

export const Button = ({
  label,
  variant = 'contained',
  size = 'medium',
  color = 'primary',
  labelColor,
  labelSize,
  onClick,
  disabled = false,
  startIcon,
  endIcon,
  iconSize = '20px',
  onlyIcon = false,
  disableUppercase = false,
  disableRipple = false,
  disableHover = false,
  loading = false,
  loadingPosition = 'center',
  sx = {},
  ...props
}) => {
  const sharedSx = {
    textTransform: disableUppercase ? 'none' : undefined,
    ...(disableHover && {
      '&:hover': {
        backgroundColor: 'inherit',
        boxShadow: 'none'
      }
    }),
    ...sx
  }

  const renderLoader = () => <CircularProgress size={16} color="inherit" />

  const resizeIcon = icon => {
    if (!isValidElement(icon)) return icon

    return cloneElement(icon, {
      ...icon.props,
      fontSize: 'inherit',
      style: {
        ...icon.props.style,
        fontSize: iconSize
      }
    })
  }

  const iconProps = {
    color,
    onClick,
    size,
    disabled: disabled || loading,
    disableRipple,
    sx: sharedSx,
    ...props
  }

  const buttonProps = {
    variant,
    color,
    size,
    onClick,
    disabled: disabled || loading,
    disableRipple,
    startIcon:
      loading && loadingPosition === 'start'
        ? renderLoader()
        : resizeIcon(startIcon),
    endIcon:
      loading && loadingPosition === 'end'
        ? renderLoader()
        : resizeIcon(endIcon),
    sx: sharedSx,
    ...props
  }

  if (onlyIcon) {
    return (
      <IconButton {...iconProps}>
        {loading ? renderLoader() : startIcon || endIcon}
      </IconButton>
    )
  }

  return (
    <MuiButton {...buttonProps}>
      {loading && loadingPosition === 'center' ? (
        renderLoader()
      ) : (
        <Typography
          sx={{
            color: labelColor || 'inherit',
            fontSize: labelSize || '16px',
            textTransform: disableUppercase ? 'none' : undefined
          }}
        >
          {label}
        </Typography>
      )}
    </MuiButton>
  )
}

Button.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.oneOf(['text', 'outlined', 'contained']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  color: PropTypes.string,
  labelSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelColor: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  onlyIcon: PropTypes.bool,
  disableUppercase: PropTypes.bool,
  disableRipple: PropTypes.bool,
  disableHover: PropTypes.bool,
  loading: PropTypes.bool,
  loadingPosition: PropTypes.oneOf(['start', 'end', 'center']),
  sx: PropTypes.object,
  iconSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default Button
